import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useFilters, type StatusFilter } from '../state/FilterProvider'
import {
  dealMatchesStatus,
  isDealArchived,
  isDealCanceled,
} from '../utils/dealUrgency'
import type { Deal } from '../types/deal'

interface Props {
  allDeals: Deal[]
}

interface Option {
  key: StatusFilter
  label: string
  dot: string
}

const GROUPS: Option[][] = [
  [{ key: 'all', label: 'All', dot: 'transparent' }],
  [
    { key: 'healthy', label: 'Active', dot: '#10b981' },
    { key: 'soon', label: 'Expires soon', dot: '#eab308' },
    { key: 'expired', label: 'Expired', dot: '#dc2626' },
  ],
  [{ key: 'drafts', label: 'Drafts', dot: '#64748b' }],
  [
    { key: 'canceled', label: 'Canceled subscription', dot: '#991b1b' },
    { key: 'archived', label: 'Archived', dot: '#64748b' },
  ],
]

const LABELS: Record<StatusFilter, string> = {
  all: 'All',
  healthy: 'Active',
  soon: 'Expires soon',
  expired: 'Expired',
  drafts: 'Drafts',
  canceled: 'Canceled',
  archived: 'Archived',
}

export const StatusFilterDropdown: React.FunctionComponent<Props> = (props) => {
  const { allDeals } = props
  const { status, setStatus } = useFilters()
  const [isOpen, setIsOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return undefined
    function handleClickOutside(event: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const counts = useMemo<Record<StatusFilter, number>>(() => {
    const next: Record<StatusFilter, number> = {
      all: 0,
      healthy: 0,
      soon: 0,
      expired: 0,
      drafts: 0,
      canceled: 0,
      archived: 0,
    }
    const active = allDeals.filter((deal) => !isDealArchived(deal) && !isDealCanceled(deal))
    next.all = active.length
    next.archived = allDeals.filter((deal) => isDealArchived(deal)).length
    next.canceled = allDeals.filter((deal) => isDealCanceled(deal) && !isDealArchived(deal)).length
    for (const filter of ['healthy', 'soon', 'expired', 'drafts'] as StatusFilter[]) {
      next[filter] = active.filter((deal) => dealMatchesStatus(deal, filter)).length
    }
    return next
  }, [allDeals])

  const currentLabel = LABELS[status] ?? 'All'
  const currentCount = counts[status] ?? 0

  return (
    <div
      className={`pay-dd-wrap${isOpen ? ' open' : ''}`}
      ref={wrapRef}
      style={{ position: 'relative', alignSelf: 'center' }}
    >
      <button
        type="button"
        className={`pay-dd-toggle${status !== 'all' ? ' has-filter' : ''}`}
        onClick={() => setIsOpen((value) => !value)}
      >
        <span className="pay-dd-label">Status:</span>
        <span className="pay-dd-value">{currentLabel}</span>
        <span className="pay-dd-count">{currentCount}</span>
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className="pay-dd-menu">
        {GROUPS.map((group, groupIndex) => (
          <div key={groupIndex}>
            {groupIndex > 0 && <div className="pay-dd-sep" />}
            {group.map((option) => (
              <div
                key={option.key}
                className={`pay-dd-item${status === option.key ? ' active' : ''}`}
                onClick={() => {
                  setStatus(option.key)
                  setIsOpen(false)
                }}
              >
                <span
                  className="pay-dd-dot"
                  style={{
                    background: option.dot,
                    border: option.dot === 'transparent' ? '1px solid #cbd5e1' : undefined,
                  }}
                />
                <span className="pay-dd-text">{option.label}</span>
                <span className="chip-count">{counts[option.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
